import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { type CSSProperties, type ReactNode } from "react";

import { ApiError } from "../../../lib/api.js";
import { GTCC_AUDIT_KEY, fetchGtccAudit, type GtccAuditEntry } from "../../api/audit.js";
import {
  CELL_RULE_STYLE,
  ControlFreshBadge,
  ControlHead,
  ControlNote,
  ControlPanel,
  ControlTableWrap,
  MONO_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
} from "../control/ControlPrimitives.js";

/* A-GTCC-VIEW-ALIN-AUDIT1: die Zellen kommen vollstaendig aus den kanonischen
   `ControlPrimitives`. Lokal bleibt nur die Verbindung zweier kanonischer
   Bausteine — Zellenmass und Trennlinie der Referenz, die auf jedem `th` und
   jedem `td` steht (`GTCC_REFERENCE_2026-09-01.html:102-103`) und in dieser
   Sicht bisher ganz gefehlt hat. Die Monoschrift stand vorher lokal auf
   `--gt-sys-font-size-xs` (12 px) statt auf den 11 px der Referenz. */
const HEAD_CELL_STYLE: CSSProperties = { ...TH_STYLE, ...CELL_RULE_STYLE };

const CELL_STYLE: CSSProperties = { ...TD_STYLE, ...CELL_RULE_STYLE };

const MONO_STYLE: CSSProperties = { ...MONO_CELL_STYLE, ...CELL_RULE_STYLE };

function maskEmail(value: string): string {
  const at = value.indexOf("@");
  return at > 0 ? `${value.slice(0, at)}@…` : value;
}

function formatTime(value: string, invalidLabel: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return invalidLabel;
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return new Intl.DateTimeFormat(locale, {
    ...(sameDay ? {} : { day: "2-digit", month: "2-digit" }),
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function maskRawData(value: string): string {
  return value.replace(/\b([^\s@]+)@[^\s@]+\b/g, "$1@…");
}

function formatAuditAction(action: string | null): string {
  if (action === "member.create") return "member.created";
  if (action === "invite.create") return "invitation.created";
  return action ?? "—";
}

function formatAuditActor(actorSub: string | null): string {
  if (!actorSub) return "—";
  if (actorSub === "system-invite") return "system (invite)";
  return actorSub
    .split("-")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function detailOf(entry: GtccAuditEntry): string {
  const actorEmail = entry.actorEmail ?? "";
  const actor = actorEmail === "" ? (entry.actorSub ?? "—") : maskEmail(actorEmail);
  return maskRawData(`${actor} · ${entry.targetType ?? "—"}: ${entry.targetId ?? "—"}`);
}

/** Reference sheet lines 836–848, backed only by GET /v1/admin/audit. */
export function AuditView(): ReactNode {
  const { t, locale } = useLocale();
  const query = useQuery({
    queryKey: GTCC_AUDIT_KEY,
    queryFn: ({ signal }) => fetchGtccAudit({}, signal),
    staleTime: 30_000,
  });

  const head = (
    <ControlHead
      testId="gtcc-audit-head"
      title="Audit Log"
      subtitle={t("admin.gtcc.audit.control.subtitle")}
      fresh={
        <ControlFreshBadge text={t("admin.gtcc.audit.control.fresh")} testId="gtcc-audit-fresh" />
      }
    />
  );
  if (query.isPending)
    return (
      <Stack gap={3} data-testid="gtcc-view-audit">
        {head}
        <LoadingState label={t("admin.gtcc.audit.control.loading")} />
      </Stack>
    );
  if (query.isError)
    return (
      <Stack gap={3} data-testid="gtcc-view-audit">
        {head}
        <ErrorState
          severity="warn"
          title={t(
            query.error instanceof ApiError && query.error.status === 403
              ? "admin.gtcc.audit.forbidden"
              : "admin.gtcc.audit.audit.error",
          )}
        />
      </Stack>
    );

  // Shell-level navigation tests and a temporarily malformed upstream payload
  // must not turn a read-only cockpit surface into an uncaught render error.
  // The API adapter remains typed; this is only the view's safe empty state.
  const entries = Array.isArray(query.data?.items) ? query.data.items : [];

  return (
    <Stack gap={3} data-testid="gtcc-view-audit">
      {head}
      <ControlPanel
        testId="gtcc-audit-panel"
        title={t("admin.gtcc.audit.control.entries")}
        note={t("admin.gtcc.audit.control.filter_hint")}
      >
        {query.data.nextCursor ? (
          <ControlNote testId="gtcc-audit-truncated-note">
            {t("admin.gtcc.audit.page_truncated")}
          </ControlNote>
        ) : null}
        <ControlTableWrap testId="gtcc-audit-table">
          <table aria-label={t("admin.gtcc.audit.control.entries")} style={TABLE_STYLE}>
            <thead>
              <tr>
                <th scope="col" style={HEAD_CELL_STYLE}>
                  {t("admin.gtcc.audit.control.column.time")}
                </th>
                <th scope="col" style={HEAD_CELL_STYLE}>
                  {t("admin.gtcc.audit.control.column.actor")}
                </th>
                <th scope="col" style={HEAD_CELL_STYLE}>
                  {t("admin.gtcc.audit.control.column.action")}
                </th>
                <th scope="col" style={HEAD_CELL_STYLE}>
                  {t("admin.gtcc.audit.control.column.detail")}
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={4} style={CELL_STYLE}>
                    {t("admin.gtcc.audit.audit.empty")}
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={MONO_STYLE}>
                      {formatTime(
                        entry.createdAt,
                        t("admin.gtcc.audit.control.invalid_time"),
                        locale,
                      )}
                    </td>
                    <td style={MONO_STYLE}>{formatAuditActor(entry.actorSub)}</td>
                    <td style={CELL_STYLE}>{formatAuditAction(entry.action)}</td>
                    <td style={MONO_STYLE}>{detailOf(entry)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ControlTableWrap>
      </ControlPanel>
    </Stack>
  );
}
